import { Button, OverlayContainer, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import React, { useCallback, useMemo } from "react";
import { useMeetingUtilsContext } from "../../contexts/meetingUtils/context";
import { useSdkContext } from "../../contexts/sdk/context";
import { PopulatedSession } from "../../contexts/meetingUtils/interfaces";
import { UserCardTarget } from "../../contexts/userCard/context";
import { useFeatures } from "../../hooks/features";
import { isExpert } from "../../helpers/roles";
import { toastError } from "../../helpers/toast";
import { getNormalUser } from "../../helpers/user";
import { StringInterval } from "../utils/StringInterval";

import styles from "./UserCardPopup.module.css";

export interface UserCardPopupProps {
  /** Minimal user data carried by the clicked avatar. */
  user: UserCardTarget;
  onClose: () => void;
}

export const UserCardPopup = React.memo(({ user, onClose }: UserCardPopupProps) => {
  // Avatars also live outside a meeting (top bar, settings): the card then
  // shows the identity alone, without presence nor highlight actions.
  const meetingCtx = useMeetingUtilsContext({ raiseIfUndefined: false });
  const populated = meetingCtx?.populated;
  const [session] = useMeetingSelector(meetingCtx?.containerAPI, "session");
  const sdkCtx = useSdkContext();
  const features = useFeatures();

  // Same gate as the "Interventions" side panel.
  const canManageHighlight = isExpert(session) && !!features.board;

  const populatedUser = useMemo(
    () => populated?.users.find(u => u.id === user.id),
    [populated?.users, user.id],
  );

  const { displayName, avatar } = useMemo(() => {
    if (populatedUser?.user) return getNormalUser(populatedUser.user);
    return { displayName: user.username ?? "", avatar: user.avatar ?? "" };
  }, [populatedUser, user.username, user.avatar]);

  /**
   * The session to act on: an ongoing highlight takes precedence, otherwise the
   * first session eligible for an invitation. Absent when the user left.
   */
  const highlight = useMemo((): { session: PopulatedSession; ongoing: boolean; pending: boolean } | undefined => {
    if (!populated) return undefined;
    const ongoing = populated.highlighted.ongoing.find(o => o.session.user.id === user.id);
    if (ongoing) return { session: ongoing.session, ongoing: true, pending: ongoing.pending };
    const inactive = populated.highlighted.inactive.find(s => s.user.id === user.id);
    if (inactive) return { session: inactive, ongoing: false, pending: false };
    return undefined;
  }, [populated, user.id]);

  // The user asked to come to the board and is waiting for an answer.
  const requesting = useMemo(
    () => !!populated?.highlighted.requesting.some(s => s.user.id === user.id),
    [populated, user.id],
  );

  const onToggleHighlight = useCallback(async () => {
    if (!highlight) return;
    const client = sdkCtx.client.getWebsocketClient();
    if (!client) return;

    const { error, response } = highlight.ongoing
      ? await client.emitter.meeting.stopHighlighting({ sessionId: highlight.session.id })
      : await client.emitter.meeting.startHighlighting({ sessionId: highlight.session.id });

    // Starting an already ongoing highlight is a harmless race, not an error.
    if ((error || !response) && !error?.message.includes("is already ongoing")) {
      toastError("Une erreur est apparue.");
    }
  }, [highlight, sdkCtx]);

  return (
    <OverlayContainer
      visible
      // Inline, not a class: OverlayContainer's own `position: relative` class
      // has the same specificity and may win depending on CSS injection order.
      style={{ position: "fixed", inset: 0, zIndex: 100 }}
      onClose={onClose}
      overlay={
        // OverlayContainer closes on any click in its overlay layer, card
        // included: the card has to keep its own clicks to itself.
        <div className={styles.card} onClick={(e) => e.stopPropagation()}>
          <PrimaryButton className={styles.closeBtn} onClick={onClose}>
            <ReactIcons.Close style={{ height: "0.8em" }} />
          </PrimaryButton>
          {populated && (
            <div className={styles.header}>
              {/* Connected while at least one of the user's sessions is running. */}
              <span className={clsx(styles.statusDot, { [styles.statusDotOffline]: !populatedUser?.active })} />
              {populatedUser?.active ? "Connecté" : "Déconnecté"}
            </div>
          )}
          <UserAvatar
            className={styles.avatar}
            mode="image"
            image={avatar}
            username={displayName}
            autoAdjustFontSize={true}
          />
          <div className={styles.name}>{displayName}</div>
          {highlight?.pending && (
            <div className={styles.pending}>
              En attente de sa confirmation
              <StringInterval delay={500} strings={["", ".", "..", "..."]} />
            </div>
          )}
          {requesting && (
            <div className={styles.pending}>Demande à monter au tableau</div>
          )}
          {canManageHighlight && highlight && (
            <Button.Simple
              className={clsx(styles.actionBtn, { [styles.actionBtnRemove]: highlight.ongoing })}
              label={highlight.ongoing ? "Renvoyer à sa place" : "Inviter à monter au tableau"}
              onClick={onToggleHighlight}
            />
          )}
        </div>
      }
    />
  );
});
